import {
  enableNsfw,
  searchUsers,
  personToChoice,
  setIsoData,
} from "@utils/app";
import {
  resourcesSettled,
  bareRoutePush,
  capitalizeFirstLetter,
  getIdFromString,
  debounce,
  getApubName,
} from "@utils/helpers";
import { scrollMixin } from "../mixins/scroll-mixin";
import {
  Choice,
  ItemIdAndRes,
  itemLoading,
  RouteDataResponse,
} from "@utils/types";
import { Component, InfernoNode } from "inferno";
import { Link, RouteComponentProps, RouterContext } from "inferno-router";
import {
  AddModToCommunity,
  AddModToCommunityResponse,
  CommunityModeratorView,
  CommunityResponse,
  CreateCommunityTag,
  DeleteCommunity,
  DeleteCommunityTag,
  EditCommunity,
  GetCommunity,
  GetCommunityResponse,
  LemmyHttp,
  MyUserInfo,
  SuccessResponse,
  CommunityTag,
  CommunityTagId,
  TransferCommunity,
  EditCommunityTag,
} from "lemmy-js-client";
import { InitialFetchRequest } from "@utils/types";
import { FirstLoadService, I18NextService } from "../../services";
import { T } from "inferno-i18next-dess";
import {
  EMPTY_REQUEST,
  HttpService,
  LOADING_REQUEST,
  RequestState,
  wrapClient,
} from "../../services/HttpService";
import { tippyMixin } from "../mixins/tippy-mixin";
import { toast } from "@utils/app";
import { getHttpBaseInternal } from "../../utils/env";
import { browserEditCommunity } from "@utils/admin-api";
import { filterLanguagesByAllowlist } from "@services/i18n-languages";
import { IRoutePropsWithFetch } from "@utils/routes";
import { isBrowser } from "@utils/browser";
import classNames from "classnames";
import { HtmlTags } from "@components/common/html-tags";
import Tabs from "@components/common/tabs";
import { CommunityForm, CommunityFormPreview } from "./community-form";
import { PersonListing } from "@components/person/person-listing";
import { MomentTime } from "@components/common/moment-time";
import ConfirmationModal from "@components/common/modal/confirmation-modal";
import { UserBadges } from "@components/common/user-badges";
import { Icon, Spinner } from "@components/common/icon";
import { amTopMod, amHigherModerator, amTopModExcludeMe } from "@utils/roles";
import { CommunityTagForm } from "./community-tag-form";
import { NoOptionI18nKeys } from "i18next";
import { CommunityLink } from "./community-link";
import { FilterChipSelect } from "@components/common/filter-chip-select";
import { removeLocalStorageMarkdown } from "@components/common/markdown-textarea";
import { aimooT } from "@utils/aimoo-i18n";

type CommunitySettingsData = RouteDataResponse<{
  communityRes: GetCommunityResponse;
}>;

interface State {
  communityRes: RequestState<GetCommunityResponse>;
  editCommunityRes: RequestState<CommunityResponse>;
  transferCommunityRes: RequestState<GetCommunityResponse>;
  deleteCommunityRes: RequestState<CommunityResponse>;
  leaveModTeamRes: RequestState<AddModToCommunityResponse>;
  purgeCommunityRes: RequestState<SuccessResponse>;
  createOrEditTagRes: ItemIdAndRes<CommunityTagId, CommunityTag>;
  deleteTagRes: ItemIdAndRes<CommunityTagId, CommunityTag>;
  isIsomorphic: boolean;
  showLeaveModTeamDialog: boolean;
  // You need to filter by the specific mod id, since this is a jsx loop
  // An undefined means hide the dialog
  showRemoveModDialog: CommunityModeratorView | undefined;
  showTransferDialog: CommunityModeratorView | undefined;
  addModSearchOptions: Choice[];
  addModSearchLoading: boolean;
  preview: CommunityFormPreview;
  previewFromForm: boolean;
}

// There are no url filters to this page, hence no props
interface Props {
  none: string;
}

type PathProps = { name: string };
type RouteProps = RouteComponentProps<PathProps> & Props;
export type CommunitySettingsFetchConfig = IRoutePropsWithFetch<
  CommunitySettingsData,
  PathProps,
  Props
>;

@scrollMixin
@tippyMixin
export class CommunitySettings extends Component<RouteProps, State> {
  private isoData = setIsoData<CommunitySettingsData>(this.context);
  state: State = {
    communityRes: EMPTY_REQUEST,
    editCommunityRes: EMPTY_REQUEST,
    transferCommunityRes: EMPTY_REQUEST,
    deleteCommunityRes: EMPTY_REQUEST,
    leaveModTeamRes: EMPTY_REQUEST,
    purgeCommunityRes: EMPTY_REQUEST,
    createOrEditTagRes: { id: 0, res: EMPTY_REQUEST },
    deleteTagRes: { id: 0, res: EMPTY_REQUEST },
    showLeaveModTeamDialog: false,
    showRemoveModDialog: undefined,
    showTransferDialog: undefined,
    isIsomorphic: false,
    addModSearchOptions: [],
    addModSearchLoading: false,
    preview: {},
    previewFromForm: false,
  };

  loadingSettled() {
    return resourcesSettled([this.state.communityRes]);
  }

  constructor(props: RouteProps, context: object) {
    super(props, context);

    // Only fetch the data if coming from another route
    if (FirstLoadService.isFirstLoad) {
      const { communityRes } = this.isoData.routeData;

      this.state = {
        ...this.state,
        isIsomorphic: true,
        communityRes,
      };
    }
  }

  fetchCommunityToken?: symbol;
  async fetchCommunity(props: RouteProps) {
    const token = (this.fetchCommunityToken = Symbol());
    this.setState({ communityRes: LOADING_REQUEST });
    const name = decodeURIComponent(props.match.params.name);
    const communityRes = await HttpService.client.getCommunity({
      name,
    });
    if (token === this.fetchCommunityToken) {
      this.setState({ communityRes });
    }
  }

  async componentWillMount() {
    if (!this.state.isIsomorphic && isBrowser()) {
      await this.fetchCommunity(this.props);
    }
  }

  async componentWillReceiveProps(
    nextProps: RouteProps & { children?: InfernoNode },
  ) {
    if (
      bareRoutePush(this.props, nextProps) ||
      this.props.match.params.name !== nextProps.match.params.name
    ) {
      await this.fetchCommunity(nextProps);
    }
  }

  static fetchInitialData = async ({
    headers,
    match: { params: props },
  }: InitialFetchRequest<PathProps, Props>): Promise<CommunitySettingsData> => {
    const client = wrapClient(
      new LemmyHttp(getHttpBaseInternal(), { headers }),
    );

    const communityName = decodeURIComponent(props.name);
    const communityForm: GetCommunity = {
      name: communityName,
    };

    const communityRes = await client.getCommunity(communityForm);

    return {
      communityRes,
    };
  };

  get documentTitle(): string {
    const cRes = this.state.communityRes;
    return cRes.state === "success"
      ? `${cRes.data.community_view.community.title} ${I18NextService.i18n.t("settings")} - ${this.isoData.siteRes.site_view.site.name}`
      : "";
  }

  render() {
    const { siteRes, myUserInfo } = this.isoData;
    const getCommunityRes =
      this.state.communityRes.state === "success" &&
      this.state.communityRes.data;
    const imageUploadDisabled =
      this.isoData.siteRes.site_view.local_site.image_upload_disabled;
    const community =
      getCommunityRes && getCommunityRes.community_view.community;
    const communityTitle =
      community?.title?.trim() || community?.name || aimooT("community");
    const preview = this.state.preview;
    const fromForm = this.state.previewFromForm;
    const previewTitle =
      (
        (fromForm ? preview.title : undefined) ??
        community?.title ??
        ""
      ).trim() || aimooT("preview_title_fallback");
    const previewSummary =
      (
        (fromForm ? preview.summary : undefined) ??
        community?.summary ??
        ""
      ).trim() || aimooT("preview_summary_fallback");
    const previewIcon =
      (fromForm ? preview.icon : undefined) ?? community?.icon;
    const previewBanner =
      (fromForm ? preview.banner : undefined) ?? community?.banner;
    const previewInitial = previewTitle.slice(0, 1);
    const previewCategory =
      (fromForm ? preview.categoryName : undefined) ??
      aimooT("no_category_selected");
    const subscribers =
      getCommunityRes && getCommunityRes.community_view.community.subscribers;

    return (
      <div className="aimoo-create-community aimoo-community-settings">
        <HtmlTags
          title={this.documentTitle}
          context={this.context as RouterContext}
        />
        <div className="aimoo-create-community__inner container-lg px-3">
          <nav
            className="aimoo-create-community__breadcrumb"
            aria-label="breadcrumb"
          >
            <Link to="/" className="aimoo-create-community__breadcrumb-link">
              {aimooT("community")}
            </Link>
            <span
              className="aimoo-create-community__breadcrumb-sep"
              aria-hidden="true"
            >
              /
            </span>
            {community ? (
              <Link
                to={`/c/${community.name}`}
                className="aimoo-create-community__breadcrumb-link"
              >
                {communityTitle}
              </Link>
            ) : (
              <span className="aimoo-create-community__breadcrumb-current">
                ??              </span>
            )}
            <span
              className="aimoo-create-community__breadcrumb-sep"
              aria-hidden="true"
            >
              /
            </span>
            <span className="aimoo-create-community__breadcrumb-current">
              {I18NextService.i18n.t("settings")}
            </span>
          </nav>

          {this.state.communityRes.state === "loading" && (
            <div className="aimoo-community-settings__loading">
              <Spinner large />
            </div>
          )}

          {getCommunityRes && (
            <div className="aimoo-create-community__layout">
              <div className="aimoo-create-community__main">
                <div className="aimoo-create-community__card">
                  <h1 className="aimoo-create-community__title">
                    {community ? (
                      <T i18nKey="x_settings">
                        <CommunityLink
                          community={community}
                          myUserInfo={myUserInfo}
                          muted={false}
                        />
                      </T>
                    ) : (
                      I18NextService.i18n.t("settings")
                    )}
                  </h1>
                  <p className="aimoo-create-community__subtitle">
                    {aimooT("community_settings_subtitle")}
                  </p>

                  <Tabs
                    tabs={[
                      {
                        key: "community",
                        label: I18NextService.i18n.t("community"),
                        getNode: isSelected => (
                          <div
                            className={classNames(
                              "tab-pane show aimoo-community-settings__tab",
                              { active: isSelected },
                            )}
                            role="tabpanel"
                            id="community-tab-pane"
                          >
                            <CommunityForm
                              communityView={getCommunityRes.community_view}
                              allLanguages={filterLanguagesByAllowlist(
                                siteRes.all_languages,
                              )}
                              siteLanguages={siteRes.discussion_languages}
                              communityLanguages={
                                getCommunityRes.discussion_languages
                              }
                              onEdit={form => handleEditCommunity(this, form)}
                              createOrEditLoading={
                                this.state.editCommunityRes.state === "loading"
                              }
                              onDelete={deleted =>
                                handleDeleteCommunity(this, deleted)
                              }
                              deleteLoading={
                                this.state.deleteCommunityRes.state === "loading"
                              }
                              enableNsfw={enableNsfw(siteRes)}
                              myUserInfo={myUserInfo}
                              imageUploadDisabled={imageUploadDisabled}
                              onPreviewChange={next =>
                                this.setState({
                                  preview: next,
                                  previewFromForm: true,
                                })
                              }
                            />
                          </div>
                        ),
                      },
                      {
                        key: "moderators",
                        label: I18NextService.i18n.t("mods"),
                        getNode: isSelected => (
                          <div
                            className={classNames(
                              "tab-pane aimoo-community-settings__tab",
                              { active: isSelected },
                            )}
                            role="tabpanel"
                            id="users-tab-pane"
                          >
                            {this.moderatorsTab()}
                          </div>
                        ),
                      },
                      {
                        key: "tags",
                        label: I18NextService.i18n.t("tags"),
                        getNode: isSelected => (
                          <div
                            className={classNames(
                              "tab-pane aimoo-community-settings__tab",
                              { active: isSelected },
                            )}
                            role="tabpanel"
                            id="taglines-tab-pane"
                          >
                            {this.tagsTab()}
                          </div>
                        ),
                      },
                    ]}
                  />
                </div>
              </div>

              <aside className="aimoo-create-community__preview">
                <div className="aimoo-create-community__preview-sticky">
                  <h2 className="aimoo-create-community__preview-heading">
                    <Icon icon="eye" classes="icon-inline me-1" />
                    {aimooT("live_preview")}
                  </h2>
                  <div className="aimoo-create-community__preview-card">
                    <div
                      className="aimoo-create-community__preview-banner"
                      style={
                        previewBanner
                          ? {
                              backgroundImage: `url(${previewBanner})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }
                          : undefined
                      }
                    />
                    <div className="aimoo-create-community__preview-body">
                      {previewIcon ? (
                        <img
                          src={previewIcon}
                          alt=""
                          className="aimoo-create-community__preview-avatar"
                        />
                      ) : (
                        <div className="aimoo-create-community__preview-avatar aimoo-create-community__preview-avatar--fallback">
                          {previewInitial}
                        </div>
                      )}
                      <h3 className="aimoo-create-community__preview-title">
                        {previewTitle}
                      </h3>
                      <p className="aimoo-create-community__preview-summary">
                        {previewSummary}
                      </p>
                      <div className="aimoo-create-community__preview-meta">
                        <span>
                          <Icon icon="groups" classes="icon-inline me-1" />
                          {aimooT("members_count", {
                            count: subscribers ?? 0,
                          })}
                        </span>
                        <span>{previewCategory}</span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary w-100 aimoo-create-community__preview-join"
                        disabled
                      >
                        {aimooT("join")}
                      </button>
                    </div>
                  </div>
                  <p className="aimoo-create-community__preview-note">
                    {aimooT("preview_hint")}
                  </p>
                </div>
              </aside>
            </div>
          )}
        </div>
      </div>
    );
  }

  tagsTab() {
    const res =
      this.state.communityRes.state === "success" &&
      this.state.communityRes.data;

    return (
      res && (
        <div className="aimoo-community-settings__section">
          <div className="aimoo-community-settings__section-head">
            <h2 className="aimoo-community-settings__section-title">
              {I18NextService.i18n.t("tags")}
            </h2>
            <p className="aimoo-community-settings__section-desc">
              {aimooT("community_tags_desc")}
            </p>
          </div>
          <div className="aimoo-community-settings__tags">
            {res.community_view.tags.map(t => (
              <div
                key={`community-tag-form-${t.id}`}
                className="aimoo-community-settings__tag-card"
              >
                <CommunityTagForm
                  tag={t}
                  onEdit={form => handleEditTag(this, form)}
                  onDeleteOrRestore={form => handleDeleteTag(this, form)}
                  createOrEditLoading={
                    itemLoading(this.state.createOrEditTagRes) === t.id
                  }
                  deleteOrRestoreLoading={
                    itemLoading(this.state.deleteTagRes) === t.id
                  }
                  myUserInfo={this.isoData.myUserInfo}
                />
              </div>
            ))}
            <div className="aimoo-community-settings__tag-card aimoo-community-settings__tag-card--create">
              <CommunityTagForm
                onCreate={form => handleCreateTag(this, form)}
                communityId={res.community_view.community.id}
                createOrEditLoading={
                  itemLoading(this.state.createOrEditTagRes) === 0
                }
                myUserInfo={this.isoData.myUserInfo}
              />
            </div>
          </div>
        </div>
      )
    );
  }

  moderatorsTab() {
    const res =
      this.state.communityRes.state === "success" &&
      this.state.communityRes.data;

    const myUserInfo = this.isoData.myUserInfo;

    return (
      <div className="aimoo-community-settings__section">
        <div className="aimoo-community-settings__section-head">
          <h2 className="aimoo-community-settings__section-title">
            {capitalizeFirstLetter(I18NextService.i18n.t("mods"))}
          </h2>
          <p className="aimoo-community-settings__section-desc">
            {aimooT("community_mods_desc")}
          </p>
        </div>

        <div className="aimoo-community-settings__mods" id="mods-table">
          <div className="aimoo-community-settings__mods-head d-none d-md-grid">
            <div>{I18NextService.i18n.t("username")}</div>
            <div>{I18NextService.i18n.t("registered_date_title")}</div>
            <div>{I18NextService.i18n.t("posts")}</div>
            <div>{I18NextService.i18n.t("comments")}</div>
          </div>
          {res &&
            res.moderators.map(m => (
              <div
                className="aimoo-community-settings__mods-row"
                key={m.moderator.id}
              >
                <div className="aimoo-community-settings__mods-user">
                  <span className="aimoo-community-settings__mods-label d-md-none">
                    {I18NextService.i18n.t("username")}
                  </span>
                  <div className="aimoo-community-settings__mods-user-main">
                    <PersonListing
                      person={m.moderator}
                      banned={false}
                      myUserInfo={myUserInfo}
                      muted={false}
                    />
                    <UserBadges
                      classNames="ms-1"
                      myUserInfo={myUserInfo}
                      creator={m.moderator}
                    />
                    {amHigherModerator(res.moderators, m, myUserInfo) && (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm aimoo-community-settings__icon-btn"
                          onClick={() =>
                            this.setState({
                              showRemoveModDialog: m,
                            })
                          }
                          data-tippy-content={I18NextService.i18n.t(
                            "remove_as_mod",
                          )}
                          aria-label={I18NextService.i18n.t("remove_as_mod")}
                        >
                          <Icon icon="x" classes="icon-inline text-danger" />
                        </button>
                        <ConfirmationModal
                          show={
                            this.state.showRemoveModDialog?.moderator.id ===
                            m.moderator.id
                          }
                          message={I18NextService.i18n.t(
                            "remove_as_mod_are_you_sure",
                            {
                              user: getApubName(m.moderator),
                              community: getApubName(m.community),
                            },
                          )}
                          loadingMessage={I18NextService.i18n.t("removing_mod")}
                          onNo={() =>
                            this.setState({ showRemoveModDialog: undefined })
                          }
                          onYes={() =>
                            handleAddMod(this, {
                              community_id: res.community_view.community.id,
                              person_id: m.moderator.id,
                              added: false,
                            })
                          }
                        />
                      </>
                    )}
                    {amTopModExcludeMe(
                      m.moderator.id,
                      res.moderators,
                      myUserInfo,
                    ) && (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm aimoo-community-settings__icon-btn"
                          onClick={() =>
                            this.setState({
                              showTransferDialog: m,
                            })
                          }
                          data-tippy-content={I18NextService.i18n.t(
                            "transfer_community",
                          )}
                          aria-label={I18NextService.i18n.t(
                            "transfer_community",
                          )}
                        >
                          <Icon icon="transfer" classes="icon-inline" />
                        </button>
                        <ConfirmationModal
                          show={
                            this.state.showTransferDialog?.moderator.id ===
                            m.moderator.id
                          }
                          message={I18NextService.i18n.t(
                            "transfer_community_are_you_sure",
                            {
                              user: getApubName(m.moderator),
                              community: getApubName(m.community),
                            },
                          )}
                          loadingMessage={I18NextService.i18n.t(
                            "transferring_community",
                          )}
                          onNo={() =>
                            this.setState({ showTransferDialog: undefined })
                          }
                          onYes={() =>
                            handleTransferCommunity(this, {
                              community_id: res.community_view.community.id,
                              person_id: m.moderator.id,
                            })
                          }
                        />
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <span className="aimoo-community-settings__mods-label d-md-none">
                    {I18NextService.i18n.t("registered_date_title")}
                  </span>
                  <MomentTime published={m.moderator.published_at} />
                </div>
                <div>
                  <span className="aimoo-community-settings__mods-label d-md-none">
                    {I18NextService.i18n.t("posts")}
                  </span>
                  {m.moderator.post_count}
                </div>
                <div>
                  <span className="aimoo-community-settings__mods-label d-md-none">
                    {I18NextService.i18n.t("comments")}
                  </span>
                  {m.moderator.comment_count}
                </div>
              </div>
            ))}
        </div>

        <div className="aimoo-community-settings__appoint">
          <h3 className="aimoo-community-settings__appoint-title">
            {I18NextService.i18n.t("appoint_mod")}
          </h3>
          <FilterChipSelect
            label={"appoint_mod"}
            multiple={false}
            allOptions={this.state.addModSearchOptions}
            selectedOptions={[]}
            onSelect={choices => handleAddModSelect(this, choices)}
            onSearch={res => handleAddModSearch(this, res)}
          />
        </div>
        {res && !amTopMod(res.moderators, myUserInfo) && (
          <div className="aimoo-community-settings__leave">
            {this.leaveModTeam()}
            <ConfirmationModal
              message={I18NextService.i18n.t("leave_mod_team_confirmation")}
              loadingMessage={I18NextService.i18n.t("leaving_mod_team")}
              onNo={() => handleToggleShowLeaveModTeamConfirmation(this)}
              onYes={() => handleLeaveModTeam(this, myUserInfo)}
              show={this.state.showLeaveModTeamDialog}
            />
          </div>
        )}
      </div>
    );
  }

  leaveModTeam() {
    return (
      <button
        onClick={() => handleToggleShowLeaveModTeamConfirmation(this)}
        className="btn btn-danger mb-2"
      >
        {this.state.leaveModTeamRes.state === "loading" ? (
          <Spinner />
        ) : (
          I18NextService.i18n.t("leave_mod_team")
        )}
      </button>
    );
  }

  updateCommunity(res: RequestState<CommunityResponse>) {
    this.setState(s => {
      if (s.communityRes.state === "success" && res.state === "success") {
        s.communityRes.data.community_view = res.data.community_view;
        s.communityRes.data.discussion_languages =
          res.data.discussion_languages;
      }
      return s;
    });
  }

  updateCommunityFull(res: RequestState<GetCommunityResponse>) {
    this.setState(s => {
      if (s.communityRes.state === "success" && res.state === "success") {
        s.communityRes.data.community_view = res.data.community_view;
        s.communityRes.data.moderators = res.data.moderators;
      }
      return s;
    });
  }

  updateModerators(res: RequestState<AddModToCommunityResponse>) {
    // Update the moderators
    this.setState(s => {
      if (s.communityRes.state === "success" && res.state === "success") {
        s.communityRes.data.moderators = res.data.moderators;
      }
      return s;
    });
  }
}

async function handleDeleteCommunity(i: CommunitySettings, deleted: boolean) {
  if (i.state.communityRes.state === "success") {
    const form: DeleteCommunity = {
      community_id: i.state.communityRes.data.community_view.community.id,
      deleted,
    };
    i.setState({ deleteCommunityRes: LOADING_REQUEST });
    i.setState({
      deleteCommunityRes: await HttpService.client.deleteCommunity(form),
    });

    if (i.state.deleteCommunityRes.state === "success") {
      toast(
        I18NextService.i18n.t(
          deleted ? "deleted_community" : "restored_community",
        ),
      );
      i.updateCommunity(i.state.deleteCommunityRes);
    }
  }
}

async function handleAddModSelect(i: CommunitySettings, choices: Choice[]) {
  const person_id = getIdFromString(choices[0].value);
  if (i.state.communityRes.state === "success" && person_id) {
    const form: AddModToCommunity = {
      person_id,
      community_id: i.state.communityRes.data.community_view.community.id,
      added: true,
    };
    await handleAddMod(i, form);
  }
}

async function handleAddMod(i: CommunitySettings, form: AddModToCommunity) {
  const addModRes = await HttpService.client.addModToCommunity(form);
  i.updateModerators(addModRes);

  i.setState({ showRemoveModDialog: undefined });
  if (addModRes.state === "success") {
    toast(I18NextService.i18n.t(form.added ? "appointed_mod" : "removed_mod"));
  }
}

async function handleEditCommunity(
  i: CommunitySettings,
  form: EditCommunity & { category_id?: number | null },
) {
  i.setState({ editCommunityRes: LOADING_REQUEST });

  i.setState({
    editCommunityRes: await browserEditCommunity({
      community_id: form.community_id as number,
      title: form.title,
      summary: form.summary,
      sidebar: form.sidebar,
      nsfw: form.nsfw,
      posting_restricted_to_mods: form.posting_restricted_to_mods,
      discussion_languages: form.discussion_languages as number[] | undefined,
      visibility: form.visibility,
      // Always send category from settings form (null clears).
      category_id: form.category_id ?? null,
    }),
  });

  if (i.state.editCommunityRes.state === "success") {
    i.updateCommunity(i.state.editCommunityRes);
    removeLocalStorageMarkdown();
    toast(I18NextService.i18n.t("saved"));
  } else if (i.state.editCommunityRes.state === "failed") {
    toast(i.state.editCommunityRes.err.message || aimooT("save_failed"), "danger");
  }
}

async function handleTransferCommunity(
  i: CommunitySettings,
  form: TransferCommunity,
) {
  i.setState({ transferCommunityRes: LOADING_REQUEST });
  i.setState({
    transferCommunityRes: await HttpService.client.transferCommunity(form),
  });

  if (i.state.transferCommunityRes.state === "success") {
    toast(I18NextService.i18n.t("transfer_community"));
    i.updateCommunityFull(i.state.transferCommunityRes);
  }
}

async function handleCreateTag(i: CommunitySettings, form: CreateCommunityTag) {
  i.setState({ createOrEditTagRes: { id: 0, res: LOADING_REQUEST } });
  const res = await HttpService.client.createCommunityTag(form);
  i.setState({
    createOrEditTagRes: {
      id: 0,
      res,
    },
  });

  if (res.state === "success") {
    toast(I18NextService.i18n.t("community_tag_created"));
    // Need to refetch community to update tags
    await i.fetchCommunity(i.props);
  } else if (res.state === "failed") {
    toast(I18NextService.i18n.t(res.err.name as NoOptionI18nKeys), "danger");
  }
}

async function handleEditTag(i: CommunitySettings, form: EditCommunityTag) {
  i.setState({
    createOrEditTagRes: { id: form.tag_id, res: LOADING_REQUEST },
  });
  const res = await HttpService.client.editCommunityTag(form);
  i.setState({
    createOrEditTagRes: { id: form.tag_id, res },
  });

  // Need to refetch community to update tags
  await i.fetchCommunity(i.props);

  if (res.state === "success") {
    toast(I18NextService.i18n.t("community_tag_edited"));
  }
}

async function handleDeleteTag(i: CommunitySettings, form: DeleteCommunityTag) {
  i.setState({ deleteTagRes: { id: form.tag_id, res: LOADING_REQUEST } });
  const res = await HttpService.client.deleteCommunityTag(form);
  i.setState({
    deleteTagRes: { id: form.tag_id, res },
  });

  if (res.state === "success") {
    toast(I18NextService.i18n.t("community_tag_deleted"));
    // Need to refetch community to update tags
    await i.fetchCommunity(i.props);
  }
}

// TODO this also needs some kind of smart user picker
// async function handleBanFromCommunity(
//   i: CommunitySettings,
//   form: BanFromCommunity,
// ) {
//   const banRes = await HttpService.client.banFromCommunity(form);

//   // TODO
//   // i.updateBanFromCommunity(banRes, form.ban);
// }

async function handleLeaveModTeam(
  i: CommunitySettings,
  myUserInfo: MyUserInfo | undefined,
) {
  const myId = myUserInfo?.local_user_view.person.id;
  if (i.state.communityRes.state === "success" && myId) {
    const form: AddModToCommunity = {
      community_id: i.state.communityRes.data.community_view.community.id,
      person_id: myId,
      added: false,
    };
    i.setState({ leaveModTeamRes: LOADING_REQUEST });
    i.setState({
      leaveModTeamRes: await HttpService.client.addModToCommunity(form),
    });

    if (i.state.leaveModTeamRes.state === "success") {
      toast(I18NextService.i18n.t("left_mod_team"));
      i.setState({ showLeaveModTeamDialog: false });
      const context = i.context as RouterContext;
      context.router.history.replace("/");
    }
  }
}

function handleToggleShowLeaveModTeamConfirmation(i: CommunitySettings) {
  i.setState({ showLeaveModTeamDialog: !i.state.showLeaveModTeamDialog });
}

const handleAddModSearch = debounce(
  async (i: CommunitySettings, text: string) => {
    const currentMods =
      (i.state.communityRes.state === "success" &&
        i.state.communityRes.data.moderators) ||
      [];

    i.setState({ addModSearchLoading: true });

    const newOptions: Choice[] = [];

    if (text.length > 0) {
      newOptions.push(
        ...(await searchUsers(text))
          // Filter out current mods
          .filter(
            pv =>
              !currentMods.map(cc => cc.moderator.id).includes(pv.person.id),
          )
          .map(personToChoice),
      );

      i.setState({
        addModSearchOptions: newOptions,
      });
    }

    i.setState({
      addModSearchLoading: false,
    });
  },
);
